/**
 *
 * @module purchaseManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/purchase.md#x-bamtech-refresh-access-token-header
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';
import AccessStatus from './accessStatus';

const Util = Services.Util;
const PurchaseManagerConfiguration = Services.Configuration.PurchaseManagerConfiguration;
const PurchaseClient = Services.Purchase.PurchaseClient;
const ReceiptCredentials = Services.Purchase.ReceiptCredentials;
const TokenManager = Token.TokenManager;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class PurchaseManager
 * @desc Manages the redemption of purchases made through platform-specific stores.
 *
 */
export default class PurchaseManager {

    /**
     *
     * @param {SDK.Services.Configuration.PurchaseManagerConfiguration} purchaseManagerConfiguration
     * @param {SDK.Services.Purchase.PurchaseClient} purchaseClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.TokenManager} tokenManager
     *
     */
    constructor(purchaseManagerConfiguration, purchaseClient, logger = Logging, tokenManager) {

        const errorMessage = {
            ctor: 'PurchaseManager(purchaseManagerConfiguration, purchaseClient, logger, tokenManager)',
            get purchaseManagerConfiguration() {
                return `${this.ctor} needs SDK.Services.Configuration.PurchaseManagerConfiguration instance`;
            },
            get purchaseClient() {
                return `${this.ctor} needs SDK.Services.PurchaseClient instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            }
        };

        instanceStrict(purchaseManagerConfiguration, PurchaseManagerConfiguration, errorMessage.purchaseManagerConfiguration, InvalidArgumentException); // eslint-disable-line
        instanceStrict(purchaseClient, PurchaseClient, errorMessage.purchaseClient, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.PurchaseManagerConfiguration}
         *
         */
        this.config = purchaseManagerConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Services.Purchase.PurchaseClient}
         *
         */
        this.client = purchaseClient;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        this.logger.log(PurchaseManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Purchase.ReceiptCredentails} receiptCredentials - Information
     * about the purchase made in the store.
     * @param {SDK.Purchase.RedeemRetryHandler} [handler=null]
     * @desc Uses the receipt provided to verify if pertains to a valid purchase
     * @returns {Promise<AccessStatus>}
     *
     */
    redeemPurchases(receiptCredentials, handler) {

        const errorMessage = {
            method: 'PurchaseManager.redeemPurchases(receiptCredentials)',
            get receiptCredentials() {
                return `${this.method} needs SDK.Services.Purchase.ReceiptCredentials instance`;
            }
        };

        instanceStrict(receiptCredentials, ReceiptCredentials, errorMessage.receiptCredentials, InvalidArgumentException); // eslint-disable-line

        const { client, accessToken, config } = this;
        const retryPolicy = Util.getSafe(() => config.extras.retryPolicy);

        return client.redeemPurchases(receiptCredentials, accessToken).then((purchaseActivationResult) => {

            const accessStatus = new AccessStatus(purchaseActivationResult, retryPolicy, handler);
            const forceRefresh = purchaseActivationResult.refreshAccessToken;

            if (forceRefresh) {

                return this.tokenManager.refreshAccessToken(forceRefresh).then(() => {
                    return Promise.resolve(accessStatus);
                });

            }

            return Promise.resolve(accessStatus);

        });

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Purchase.ReceiptCredentails} receiptCredentials - Information
     * about the purchase made in the store.
     * @desc Uses the receipt provided to verify if it pertains to a valid purchase
     * @returns {Promise<AccessStatus>}
     *
     */
    restorePurchases(receiptCredentials) {

        const errorMessage = {
            method: 'PurchaseManager.restorePurchases(receiptCredentials)',
            get receiptCredentials() {
                return `${this.method} needs SDK.Services.Purchase.ReceiptCredentials instance`;
            }
        };

        instanceStrict(receiptCredentials, ReceiptCredentials, errorMessage.receiptCredentials, InvalidArgumentException); // eslint-disable-line

        const { client, accessToken, config } = this;
        const retryPolicy = Util.getSafe(() => config.extras.retryPolicy);

        return client.restorePurchases(receiptCredentials, accessToken).then((purchaseActivationResult) => {

            const accessStatus = new AccessStatus(purchaseActivationResult, retryPolicy);
            const forceRefresh = purchaseActivationResult.refreshAccessToken;

            if (forceRefresh) {

                return this.tokenManager.refreshAccessToken(forceRefresh).then(() => {
                    return Promise.resolve(accessStatus);
                });

            }

            return Promise.resolve(accessStatus);

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/purchase/purchaseManager.js